import { useState } from "react";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { useVendors } from "@/components/VendorPicker";
import { useLoad } from "@/hooks/useData";
import { api, downloadFile, errMsg } from "@/lib/api";
import { dateLabel } from "@/lib/format";

const GROUP = "border-l-[1.5px] border-l-[#9CA3AF]";
const INNER = "border-l border-l-[#E5E7EB]";
const TOTAL = "border-l-2 border-l-[#9CA3AF]";
const GREY = "bg-[#F3F4F6]";
const CELL = "px-3 py-2 border-b border-b-[#E5E7EB]";
const NUM = "text-right font-mono tabular-nums";
const PROD = "sticky left-0 border-r-2 border-r-[#9CA3AF] shadow-[4px_0_6px_-2px_rgba(0,0,0,0.12)]";
const TH = "px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#374151] whitespace-normal";
const H1 = { position: "sticky", top: 0, zIndex: 3, height: 37 };
const H2 = { position: "sticky", top: 37, zIndex: 3 };
const ORD = "text-[#374151]";
const REC = "text-[#15803D]";

const Qty = ({ v, refs, cls, tone, testid }) => (
  <td className={`${CELL} ${NUM} ${tone} ${cls} ${v ? "cursor-pointer hover:bg-primary/10" : ""}`} title={v ? refs?.join("\n") : undefined} data-testid={testid}
    onClick={() => v && toast(refs.join(" · "))}>{v || ""}</td>
);

const Filters = ({ f, setF, vendors }) => (
  <div className="mb-4 flex flex-wrap items-center gap-2">
    <div className="relative w-full sm:w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search product or SKU" className="h-9 pl-8" data-testid="ledger-search" /></div>
    <SimpleSelect value={f.vendor} onChange={(v) => setF({ ...f, vendor: v })} testid="ledger-vendor" className="h-9 w-full sm:w-56" options={[["all", "All vendors"], ...vendors.map((v) => [v.name, v.name])]} />
    <label className="flex items-center gap-1 text-xs">PO date from<Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="h-9 w-40" data-testid="ledger-from" /></label>
    <label className="flex items-center gap-1 text-xs">to<Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="h-9 w-40" data-testid="ledger-to" /></label>
    <label className="flex items-center gap-2 text-xs"><Checkbox checked={f.pending} onCheckedChange={(v) => setF({ ...f, pending: !!v })} data-testid="ledger-pending-only" /> Only products with pending</label>
  </div>
);

const Head = ({ data, tail }) => (
  <thead>
    <tr>
      <th className={`${TH} ${PROD} ${GREY} text-left border-b border-b-[#D1D5DB] min-w-[150px] max-w-[170px] sm:min-w-[260px] sm:max-w-none`} style={{ ...H1, zIndex: 6 }}>DATE</th>
      {data.has_opening && <th className={`${TH} ${GROUP} ${GREY} text-center border-b border-b-[#D1D5DB]`} style={H1}>Opening</th>}
      {data.dates.map((d) => <th key={d} colSpan={2} className={`${TH} ${GROUP} ${GREY} text-center border-b border-b-[#D1D5DB]`} style={H1} data-testid={`ledger-date-${d}`}>{dateLabel(d)}</th>)}
      <th colSpan={tail.length} className={`${TH} ${TOTAL} ${GREY} text-center border-b border-b-[#D1D5DB]`} style={H1}>TOTAL</th>
    </tr>
    <tr>
      <th className={`${TH} ${PROD} bg-white text-left border-b-2 border-b-[#9CA3AF]`} style={{ ...H2, zIndex: 6 }}>Product</th>
      {data.has_opening && <th className={`${TH} ${GROUP} bg-white text-right border-b-2 border-b-[#9CA3AF]`} style={H2}>Received QTY</th>}
      {data.dates.map((d) => [
        <th key={`${d}o`} className={`${TH} ${GROUP} bg-white text-right border-b-2 border-b-[#9CA3AF]`} style={H2}>Ordered QTY</th>,
        <th key={`${d}r`} className={`${TH} ${INNER} bg-white text-right border-b-2 border-b-[#9CA3AF]`} style={H2}>Received QTY</th>])}
      {tail.map((t, i) => <th key={t} className={`${TH} ${i === 0 ? TOTAL : INNER} ${GREY} text-right border-b-2 border-b-[#9CA3AF]`} style={H2}>{t}</th>)}
    </tr>
  </thead>
);

const Row = ({ r, ri, data }) => {
  const bg = ri % 2 ? "bg-[#F9FAFB]" : "bg-white";
  return (
    <tr className={bg} data-testid={`ledger-row-${ri}`}>
      <td className={`${CELL} ${PROD} ${bg} z-[4] text-xs`}><div className="product-name font-medium">{r.name}</div>{r.sku && <div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div>}</td>
      {data.has_opening && <Qty v={r.opening.r} refs={r.opening.refs} cls={GROUP} tone={REC} testid={`ledger-opening-${ri}`} />}
      {data.dates.map((d) => [
        <Qty key={`${d}o`} v={r.cells[d]?.o} refs={r.cells[d]?.orefs} cls={GROUP} tone={ORD} testid={`ledger-o-${ri}-${d}`} />,
        <Qty key={`${d}r`} v={r.cells[d]?.r} refs={r.cells[d]?.rrefs} cls={INNER} tone={REC} testid={`ledger-r-${ri}-${d}`} />])}
      <td className={`${CELL} ${NUM} ${TOTAL} ${GREY} ${ORD} font-semibold`} data-testid={`ledger-total-order-${ri}`}>{r.total_order}</td>
      <td className={`${CELL} ${NUM} ${INNER} ${GREY} ${REC} font-semibold`} data-testid={`ledger-total-received-${ri}`}>{r.total_received}</td>
      {data.has_short && <td className={`${CELL} ${NUM} ${INNER} ${GREY}`}>{r.short || ""}</td>}
      <td className={`${CELL} ${NUM} ${INNER} ${GREY} font-bold ${r.pending ? "text-amber-600" : "text-emerald-600"}`} data-testid={`ledger-pending-${ri}`}>{r.pending}</td>
    </tr>
  );
};

const Foot = ({ data, sum }) => {
  const F = `px-3 py-2 sticky bottom-0 ${GREY} border-t-2 border-t-[#9CA3AF] font-semibold`;
  return (
    <tfoot><tr>
      <td className={`${F} ${PROD} z-[5]`}>TOTAL</td>
      {data.has_opening && <td className={`${F} ${NUM} ${GROUP} ${REC}`}>{sum((r) => r.opening.r) || ""}</td>}
      {data.dates.map((d) => [
        <td key={`${d}o`} className={`${F} ${NUM} ${GROUP} ${ORD}`}>{sum((r) => r.cells[d]?.o) || ""}</td>,
        <td key={`${d}r`} className={`${F} ${NUM} ${INNER} ${REC}`}>{sum((r) => r.cells[d]?.r) || ""}</td>])}
      <td className={`${F} ${NUM} ${TOTAL} ${ORD}`} data-testid="ledger-foot-order">{sum((r) => r.total_order)}</td>
      <td className={`${F} ${NUM} ${INNER} ${REC}`} data-testid="ledger-foot-received">{sum((r) => r.total_received)}</td>
      {data.has_short && <td className={`${F} ${NUM} ${INNER}`}>{sum((r) => r.short)}</td>}
      <td className={`${F} ${NUM} ${INNER}`} data-testid="ledger-foot-pending">{sum((r) => r.pending)}</td>
    </tr></tfoot>
  );
};

export default function LedgerTab() {
  const vendors = useVendors();
  const [f, setF] = useState({ q: "", vendor: "all", from: "", to: "", pending: false });
  const params = { q: f.q, vendor: f.vendor === "all" ? undefined : f.vendor, date_from: f.from || undefined, date_to: f.to || undefined, pending_only: f.pending };
  const { data } = useLoad(() => api.get("/po-receipts/ledger", { params }).then((r) => r.data), [params]);
  const tail = data ? ["Total Order", "Total Received", ...(data.has_short ? ["Short Closed"] : []), "Pending"] : [];
  const sum = (fn) => (data?.rows || []).reduce((s, r) => s + (fn(r) || 0), 0);
  return (
    <div>
      <Filters f={f} setF={setF} vendors={vendors} />
      <div className="mb-3 flex justify-end">
        <Button size="sm" variant="outline" onClick={() => downloadFile("/po-receipts/ledger.xlsx", params, "po-product-ledger.xlsx").catch((e) => toast.error(errMsg(e)))} data-testid="ledger-export-btn"><Download className="h-4 w-4" /> Export Excel</Button>
      </div>
      {!data ? <Loading /> : !data.rows.length ? <div className="panel p-8 text-center text-sm text-muted-foreground" data-testid="ledger-empty">No orders match these filters</div> : (
        <div className="panel max-h-[calc(100vh-300px)] overflow-auto" data-testid="ledger-wrap">
          <table className="w-full border-separate border-spacing-0 text-sm" data-testid="ledger-table">
            <Head data={data} tail={tail} />
            <tbody>{data.rows.map((r, ri) => <Row key={r.product_id} r={r} ri={ri} data={data} />)}</tbody>
            <Foot data={data} sum={sum} />
          </table>
        </div>)}
    </div>
  );
}
